import React, { useEffect, useRef, useState } from 'react';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import SearchBox from '../components/SearchBox';
import SelectBox from '../components/SelectBox';
import Button from 'react-bootstrap/Button';
import {
  EmployeeNameType,
  SelectOptionType,
  TaskNameType,
  TaskStatusType,
  ViewTaskFilterType,
} from '../utils/Types';
import DatePicker from '../components/KDatePicker';
import {
  fetchEmployeeNames,
  fetchStatusDrop,
  fetchTaskNames,
  taskSelector,
} from '../features/taskSlice';
import { DEFAULT_OPTION, defaultViewTaskFilter } from '../utils/Constant';
import moment from 'moment';
import { useAppDispatch, useAppSelector } from '../store/redux-hooks';
import {
  findTaskNameAndSourceOptions,
  findTaskStatusOptions,
  getEmployeeNameList,
  onTaskNameChangeViewTask,
} from '../pages/task-assigned/TaskAssignHelper';
import MTextField from '../components/MTextField';

interface IViewTaskFilterProps {
  onApplyFilters: (viewTaskFilter: ViewTaskFilterType) => void;
}

const ViewTaskFilter: React.FunctionComponent<IViewTaskFilterProps> = ({
  onApplyFilters,
}: IViewTaskFilterProps) => {
  const dispatch = useAppDispatch();
  const initialized = useRef(false);
  const [optionTaskName, setOptionTaskName] = useState<SelectOptionType[]>([]);
  const [optionEmployeeName, setOptionEmployeeName] = useState<
    SelectOptionType[]
  >([]);
  const [optionTaskStatus, setOptionTaskStatus] = useState<SelectOptionType[]>(
    []
  );
  const [optionSource, setOptionSource] = useState<SelectOptionType[]>();
  const [viewTaskFilter, setViewTaskFilter] = useState<ViewTaskFilterType>(
    defaultViewTaskFilter
  );
  const taskNameList: TaskNameType[] =
    useAppSelector(taskSelector).taskNameList;
  const employeeList: EmployeeNameType[] =
    useAppSelector(taskSelector).employeeList;
  const tasKStatusList: TaskStatusType[] =
    useAppSelector(taskSelector).taskStatusList;
  const onResetFilter = () => {
    onApplyFilters(defaultViewTaskFilter);
    setViewTaskFilter(defaultViewTaskFilter);
  };

  const fetchTaskNamesAndEmployeeNames = () => {
    dispatch(fetchTaskNames());
    dispatch(fetchEmployeeNames());
    dispatch(fetchStatusDrop({ module: 3 }));
  };
  useEffect(() => {
    const data = findTaskNameAndSourceOptions(taskNameList);
    setOptionTaskName(data.taskNames);
    setOptionSource(data.source);
  }, [taskNameList]);
  useEffect(() => {
    setOptionEmployeeName(getEmployeeNameList(employeeList));
  }, [employeeList]);
  useEffect(() => {
    const array = findTaskStatusOptions(tasKStatusList);
    if (array?.length) {
      setOptionTaskStatus(array);
    }
  }, [tasKStatusList]);
  useEffect(() => {
    if (!initialized.current) {
      initialized.current = true;
      fetchTaskNamesAndEmployeeNames();
    }
  }, []);
  return (
    <div className="border rounded ps-3 pt-2 pe-3 pb-3">
      <Row>
        <Col xs={6} md={2}>
          <SearchBox
            label="Task Id"
            placeHolder="Search"
            value={viewTaskFilter.taskId}
            onChange={(e: any) => {
              setViewTaskFilter({
                ...viewTaskFilter,
                taskId: e.target.value,
              });
            }}
          />
        </Col>
        <Col xs={6} md={3}>
          <SelectBox
            label="Task Name"
            value={viewTaskFilter.taskName}
            options={optionTaskName}
            onChange={(value: any) => {
              onTaskNameChangeViewTask(
                value,
                taskNameList,
                viewTaskFilter,
                setViewTaskFilter
              );
            }}
          />
        </Col>
        {viewTaskFilter?.source?.value === 7 && (
          <Col xs={6} md={3}>
            <label className="mb-1 text-start search-label form-label"></label>
            <MTextField
              label="Enter Custom Task Name"
              value={viewTaskFilter.customTaskName}
              onChange={(e: any) => {
                setViewTaskFilter({
                  ...viewTaskFilter,
                  customTaskName: e.target.value,
                });
              }}
            />
          </Col>
        )}
        <Col xs={6} md={2}>
          <SelectBox
            label="Task Type"
            options={optionSource}
            className="css-select-bg"
            value={viewTaskFilter.source}
            onChange={(value: any) => {
              setViewTaskFilter({
                ...viewTaskFilter,
                taskName: DEFAULT_OPTION,
                source: value,
                customTaskName:
                  value?.value != 7 ? '' : viewTaskFilter.customTaskName,
              });
            }}
          />
        </Col>
        <Col xs={6} md={3}>
          <SelectBox
            label="Assign To"
            options={optionEmployeeName}
            className="css-select-bg"
            value={viewTaskFilter.assignedTo}
            onChange={(event: any) => {
              setViewTaskFilter({
                ...viewTaskFilter,
                assignedTo: event,
              });
            }}
          />
        </Col>
        <Col xs={6} md={2}>
          <SelectBox
            label="Status"
            options={optionTaskStatus}
            className="css-select-bg"
            value={viewTaskFilter.status}
            onChange={(event: any) => {
              setViewTaskFilter({
                ...viewTaskFilter,
                status: event,
              });
            }}
          />
        </Col>
      </Row>
      <Row>
        <Col xs={6} md={3}>
          <DatePicker
            label="Date From"
            value={viewTaskFilter.fromDate}
            minDate={moment().subtract(1, 'year').toDate()}
            maxDate={moment().add(90, 'days').toDate()}
            onChange={(date) => {
              const maxAllowedToDate = moment(date).add(89, 'days'); // 90 days from selected fromDate
              const finalMaxToDate = moment.min(
                maxAllowedToDate,
                moment().add(90, 'days')
              ); // Cap it at today + 90 days

              let newToDate = viewTaskFilter.toDate;

              if (
                newToDate &&
                (moment(newToDate).isAfter(finalMaxToDate) ||
                  moment(newToDate).isBefore(moment(date)))
              ) {
                newToDate = undefined;
              }

              setViewTaskFilter({
                ...viewTaskFilter,
                fromDate: date,
                toDate: newToDate,
              });
            }}
          />
        </Col>

        <Col xs={6} md={3}>
          <DatePicker
            label="Date To"
            minDate={viewTaskFilter.fromDate}
            maxDate={
              viewTaskFilter.fromDate
                ? moment
                    .min(
                      moment(viewTaskFilter.fromDate).add(89, 'days'),
                      moment().add(90, 'days')
                    )
                    .toDate()
                : moment().add(90, 'days').toDate()
            }
            value={viewTaskFilter.toDate}
            onChange={(date) => {
              setViewTaskFilter({
                ...viewTaskFilter,
                toDate: date,
              });
            }}
          />
        </Col>

        <Col xs={6} md={3} className="d-flex align-items-center pt-1">
          <Button
            variant="primary"
            className="cl-btn-primary"
            onClick={() => {
              onApplyFilters(viewTaskFilter);
            }}
          >
            Search
          </Button>
          <Button
            variant="outline-primary"
            className="mx-2 cl-btn-outline-primary"
            onClick={() => {
              onResetFilter();
            }}
          >
            Reset
          </Button>
        </Col>
      </Row>
    </div>
  );
};

export default ViewTaskFilter;
